<template>
    <div>
        <div v-if="entityRecord.activeTask" class="panel-section-status mt-0" :class="['job-status-'+entityRecord.activeTask.status]">
            <type-chip :type="entityRecord.activeTask.status" entity="Job" :object="entityRecord" size="large" variant="text"></type-chip>
            <div><active-task-status :job="entityRecord"></active-task-status></div>
        </div>
        <v-table density="compact" class="text-caption">
            <tbody v-if="entityRecord.activeTask">
                <tr v-for="time in entityRecord.activeTask.jobTaskTimes" :key="time.id" class="time-row">
                    <td colspan="3">
                        <div class="d-flex justify-space-between">
                            <div><v-icon icon="mdi-timeline-clock-outline"></v-icon> <b>{{ numberWithCommas($dayjs(time.endTime).diff($dayjs(time.startTime), 'minute'), 0) }}mins</b></div>
                            <div class="text-right">{{ $dayjs(time.startTime).format('hh:mm') }} - {{ $dayjs(time.endTime).format('hh:mma') }}</div>
                        </div>
                    </td>
                </tr>
                <tr v-if="entityRecord.outstandingSubtotal != entityRecord.jobSubTotal && entityRecord.outstandingSubtotal != 0">
                    <th class="w-20">Oustanding</th>
                    <td class="w-80">
                        ${{ numberWithCommas(entityRecord.outstandingSubtotal, 2) }}
                        <v-chip v-if="entityRecord.remainingPercentage && entityRecord.remainingPercentage != 1"
                                :prepend-icon="entityRecord.remainingPercentage > 0 ? 'mdi-list-status' : 'mdi-check-circle'"
                                size="small"
                                :color="entityRecord.remainingPercentage > 0 ? 'warning' : 'success'">{{ numberWithCommas(entityRecord.remainingPercentage*100, 0) }}%</v-chip>
                    </td>
                </tr>
            </tbody>
        </v-table>
        <job-task-rework-summary v-if="entityRecord.activeTask"
                                 :task="entityRecord.activeTask"
                                 :nuwall="entityRecord.contact?.nuwall"
                                 class="pa-1"></job-task-rework-summary>
        <strong class="panel-section-header mt-0">
            Timeline
        </strong>
        <div v-if="entityRecord.archivedJobTasks">
            <v-table density="compact" class="text-caption task-table">
                <tbody>
                    <tr>
                        <th class="w-40 pa-1">Task Completed</th>
                        <th class="w-40 pa-1 text-right">Date Completed</th>
                        <th class="w-20 text-right pa-1">$</th>
                    </tr>
                </tbody>
                <tbody v-for="archivedTask in entityRecord.archivedJobTasks" :key="archivedTask.id">
                    <tr>
                        <td class="pa-1">{{ archivedTask.name }} <entity-tag-chip :object="archivedTask" class="ml-1"></entity-tag-chip></td>
                        <td class="text-right pa-1"><span v-if="archivedTask.targetStartDate">{{ $dayjs(archivedTask.targetStartDate).format('DD-MM-YYYY') }}</span></td>
                        <td class="text-right pa-1"><span v-if="archivedTask.estimatedSubtotalCompleted">${{ numberWithCommas(archivedTask.estimatedSubtotalCompleted, 2) }}</span></td>
                    </tr>
                    <tr v-if="archivedTask.reworkReasonSummary">
                        <td colspan="3" class="pa-1">
                            <job-task-rework-summary :task="archivedTask" :nuwall="entityRecord.contact?.nuwall"></job-task-rework-summary>
                        </td>
                    </tr>
                    <tr v-for="time in archivedTask.jobTaskTimes" :key="time.id" class="time-row">
                        <td colspan="3">
                            <div class="d-flex justify-space-between">
                                <div><v-icon icon="mdi-timeline-clock-outline"></v-icon> <b>{{ numberWithCommas($dayjs(time.endTime).diff($dayjs(time.startTime), 'minute'), 0) }}mins</b></div>
                                <div class="text-right">{{ $dayjs(time.startTime).format('hh:mm') }} - {{ $dayjs(time.endTime).format('hh:mma') }}</div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </div>
        <strong class="panel-section-header mt-0">
            Colour
        </strong>
        <v-table density="compact" class="text-caption">
            <tbody>
                <tr>
                    <td class="w-50">{{ entityRecord.colour?.name  }}</td>
                    <td class="w-50 text-right">
                        {{ numberWithCommas(entityRecord.totalPowderRequired, 1) }} Kgs
                    </td>
                </tr>
                <tr>
                    <td><v-icon icon="mdi-timeline-clock-outline"></v-icon> Line 1 Est.<br /> {{ numberWithCommas(entityRecord.lineOneEstimatedCoatingTime, 1) }} mins</td>
                    <td class="text-right">
                        <v-icon icon="mdi-timeline-clock-outline"></v-icon> Line 2 Est.<br/> {{ numberWithCommas(entityRecord.lineTwoEstimatedCoatingTime, 1) }} mins
                    </td>
                </tr>
            </tbody>
        </v-table>
        <strong class="panel-section-header mt-0">
            Details
        </strong>
        <v-table density="compact" class="text-caption">
            <tbody>
                <tr align="right">
                    <th class="w-20">Scheduled Pretreatment Date</th>
                    <td class="w-80">
                        <v-text-field type="date"
                                      density="compact"
                                      hide-details="auto"
                                      clearable
                                      @change="updateScheduledPretreatmentDate"
                                      v-model="scheduledPretreatmentDateInput"></v-text-field>
                    </td>
                </tr>
                <tr align="right" :class="getTagClass(entityRecord.jobTag)">
                    <th class="w-20">Tag</th>
                    <td class="w-80">
                        <v-select v-model="entityRecord.jobTag"
                                  density="compact"
                                  hide-details
                                  @update:modelValue="updateJob('jobTag')"
                                  :items="$constants.jobs.tags.map(item => item.name)"></v-select>
                    </td>
                </tr>
                <tr align="right">
                    <th class="w-20">Packaging</th>
                    <td class="w-80">
                        <v-select v-model="entityRecord.packaging"
                                  density="compact"
                                  hide-details
                                  @update:modelValue="updateJob('packaging')"
                                  :items="$constants.jobs.packaging"></v-select>
                    </td>
                </tr>
                <tr align="right">
                    <td colspan="2" class="text-right">
                        <v-checkbox v-model="entityRecord.warrantyRequired"
                                    hide-details
                                    density="compact"
                                    class="reverse-checkbox"
                                    @update:modelValue="updateJob('warrantyRequired')"
                                    label="Warranty Required?"></v-checkbox>
                    </td>
                </tr>
                <tr v-if="entityRecord.warrantyRequired" align="right">
                    <th class="w-20">Warranty Status</th>
                    <td class="w-80">
                        <v-select v-model="entityRecord.warrantyStatus"
                                  density="compact"
                                  hide-details
                                  @update:modelValue="updateJob('warrantyStatus')"
                                  item-title="title"
                                  item-value="value"
                                  :items="warrantyStatusSelect"></v-select>
                        <span class="text-caption" v-if="entityRecord.projectName">One warranty per project - this will update all <strong>{{ entityRecord.projectName }}</strong> jobs warranty status.</span>
                    </td>
                </tr>
                <tr v-if="entityRecord.customerNumber" align="right">
                    <th>Created</th>
                    <td>{{ entityRecord.dateCreated }}</td>
                </tr>
                <tr v-if="entityRecord.contact" align="right">
                    <th>Contact</th>
                    <td>
                        <contact-link :contact="entityRecord.contact"></contact-link>
                    </td>
                </tr>
                <tr v-if="entityRecord.contact && false" align="right">
                    <td colspan="2">
                        <new-email-btn :address="entityRecord.contact.accountsEmail"
                                       :name="entityRecord.contact.name"
                                       :contact="entityRecord.contact"
                                       :job="entityRecord"></new-email-btn>
                    </td>
                </tr>
                <tr v-if="entityRecord.name" align="right">
                    <th>Name</th>
                    <td>{{ entityRecord.name }}</td>
                </tr>
                <tr v-if="entityRecord.customerNumber" align="right">
                    <th>Customer#</th>
                    <td>{{ entityRecord.customerNumber }}</td>
                </tr>
                <tr align="right">
                    <th>Project</th>
                    <td>
                        <v-text-field v-model.number="entityRecord.projectName"
                                      variant="underlined"
                                      @update:modelValue="updateJob('projectName')"></v-text-field>
                    </td>
                </tr>
                <tr v-if="entityRecord.dateReceiveEstimate && !entityRecord.dateReceived" align="right">
                    <th>Estimated Receive Date</th>
                    <td>
                        {{ $dayjs(entityRecord.dateReceiveEstimate).format('DD-MM-YYYY') }}
                        <date-diff-chip :date="entityRecord.dateReceiveEstimate" :countdown="false"></date-diff-chip>
                    </td>
                </tr>
                <tr v-if="entityRecord.dateReceived" align="right">
                    <th>Receive Date</th>
                    <td>
                        <date-ago-display :date="entityRecord.dateReceived" :days-ago="entityRecord.daysSinceReceived" :countdown="false"></date-ago-display>
                    </td>
                </tr>
                <tr v-if="entityRecord.customerDateTargetCompletion && !entityRecord.dateCompleted" align="right">
                    <th>Customer Requested Target Completion Date</th>
                    <td>
                        {{ $dayjs(entityRecord.customerDateTargetCompletion).format('DD-MM-YYYY') }}
                        <date-diff-chip :date="entityRecord.customerDateTargetCompletion" :countdown="true"></date-diff-chip>
                    </td>
                </tr>
                <tr v-if="entityRecord.dateTargetCompletion && !entityRecord.dateCompleted" align="right">
                    <th>Target Completion Date</th>
                    <td>
                        {{ $dayjs(entityRecord.dateTargetCompletion).format('DD-MM-YYYY') }}
                        <date-diff-chip :date="entityRecord.dateTargetCompletion" :countdown="true"></date-diff-chip>
                    </td>
                </tr>
                <tr v-if="entityRecord.dateCompleted" align="right">
                    <th>Completion Date</th>
                    <td>{{ $dayjs(entityRecord.dateCompleted).format('DD-MM-YYYY') }}</td>
                </tr>
                <tr>
                    <th>Powder Usage</th>
                    <td>
                        <v-text-field v-model.number="entityRecord.totalPowderUsage"
                                      variant="underlined"
                                      @update:modelValue="updateJob('totalPowderUsage')"
                                      type="number"
                                      suffix="KGs"></v-text-field>
                    </td>
                </tr>
                <tr v-if="entityRecord.dollarsPerHour">
                    <th>
                        $ per hour
                    </th>
                    <td>
                        <h3>${{ numberWithCommas(entityRecord.dollarsPerHour, 2) }}</h3>
                    </td>
                </tr>
                <tr v-if="entityRecord.idealSell && entityRecord.idealSell > entityRecord.jobSubTotal">
                    <th>
                        Ideal Sell
                    </th>
                    <td>
                        <h3>${{ numberWithCommas(entityRecord.idealSell, 2) }} ex GST  <small><i>(Line {{ entityRecord.idealSellLine }})</i></small></h3>
                    </td>
                </tr>
            </tbody>
        </v-table>
        <!--<div v-if="entityRecord.status > 0 || entityRecord.archivedJobTasks">
        <strong class="panel-section-header">Tasks</strong>
        <job-tasks-small :items="entityRecord.jobTasks" :current-task="entityRecord.status" :archived="entityRecord.archivedJobTasks"></job-tasks-small>
    </div>-->
        <div v-if="entityRecord.invoices">
            <strong class="panel-section-header">
                Invoices
            </strong>
            <div class="overflow-x-hidden">
                <v-table density="compact" class="text-caption">
                    <tbody>
                        <tr>
                            <th>Invoice</th>
                            <th>Status</th>
                            <th>Amount</th>
                        </tr>
                        <tr v-for="invoice in entityRecord.invoices" :key="invoice.id">
                            <td>{{ invoice.invoiceNumber }}</td>
                            <td><invoice-status :status="invoice.status"></invoice-status></td>
                            <td>${{ numberWithCommas(invoice.totalAmount, 2) }}</td>
                        </tr>
                    </tbody>
                </v-table>
            </div>
        </div>
    </div>
</template>
<style scoped>
    .panel-section-status {
        text-align: center;
        padding-bottom: 5px;
    }
    .sub-panel-header {
        background-color: rgba(0,0,0,.03);
    }
    .text-caption tr > th {
        height: 12px !important;
    }
    .time-row {
        background: #f0f0f0;
    }
    .task-table tbody > tr > th {
        border-bottom: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
        border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
    }
    .task-table tbody > tr > td {
        border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
    }
    .job-status-0 {
        background-color: #FAFAFA;
    }
    .job-status-1 {
        background-color: #E1F5FE;
    }
    .job-status-2 {
        background-color: #FFF3E0;
    }
    .job-status-3 {
        background-color: #ECEFF1;
    }
    .job-status-4 {
        background-color: #E8EAF6;
    }
    .job-status-5 {
        background-color: #EFEBE9;
    }
    .job-status-6 {
        background-color: #E3F2FD;
    }
    .job-status-7 {
        background-color: #E3F2FD;
    }
    .job-status-8 {
        background-color: #E3F2FD;
    }
    .job-status-9 {
        background-color: #E8F5E9;
    }
</style>
<script>
    import { mapActions, mapState, mapGetters } from "vuex";
    import { numberWithCommas } from "../../mixins/numberMixins";
    import { getTagClass } from '../../mixins/jobMixins';
    function initialState() {
        return {
            saving: false,
            loading: false
        };
    }
    export default {
        name: "JobDetailsPanel",
        data: function () {
            return initialState();
        },
        mounted: function () {
        },
        props: {
        },
        methods: {
            numberWithCommas,
            ...mapActions([
                "showErrorSnack",
                "showSuccessSnack",
                "patchEntity",
            ]),
            reset: function () {
                Object.assign(this.$data, initialState());
            },
            updateJob(field){
                this.patchEntity({path:field, value: this.entityRecord[field]});
            },
            updateScheduledPretreatmentDate() {
                this.updateJob('scheduledPretreatmentDate');
            },
            getTagClass
        },
        computed: {
            ...mapState({
                entity: (state) => state.entities.entity,
                entityRecord: (state) => state.entities.entityRecord,
                entityRecordLoading: (state) => state.entities.entityRecordLoading
            }),
            ...mapGetters(['hasManagerAccess']),
            statusDueDateInput() {
                if (this.entityRecord && this.entityRecord.statusDueDate) {
                    return this.$dayjs(this.entityRecord.statusDueDate).format('YYYY-MM-DD');
                }
                return null;
            },
            scheduledPretreatmentDateInput: {
                get() {
                    if (this.entityRecord && this.entityRecord.scheduledPretreatmentDate) {
                        return this.$dayjs(this.entityRecord.scheduledPretreatmentDate).format('YYYY-MM-DD');
                    }
                    return null;
                },
                set(value) {
                    if (this.entityRecord) {
                        this.entityRecord.scheduledPretreatmentDate = value;
                    }
                }
            },
            warrantyStatusSelect() {
                return this.$constants.jobs.warrantyStatuses.map(item => ({
                    title: item.title,
                    value: item.id
                }));
            }
        }
    };
</script>